/**
 * The canon's grip — six dots — as a REAL button.
 *
 * `docs/design/2026-08-01-plan-builder-pattern-fullpage-v3.html` draws it as
 * `.grip { width: 9px; grid-template-columns: 3px 3px; gap: 3px }` with six 3px `--text-3` dots, on
 * the session card header and on every library row. PR-A1 rendered it `aria-hidden` and inert,
 * because "an affordance a screen reader announces and a keyboard cannot reach is worse than one
 * that is visibly decorative". This is what it was waiting for.
 *
 * ── It is a button because the keyboard path runs through it ─────────────────────────────────
 * View ADR §12: "The grip is a real button: Space or Enter picks up, arrows move by one position,
 * Space or Enter drops, Escape cancels." So it needs a role, a name, a tab stop and a focus ring —
 * not a `span` with a `draggable` attribute. `aria-describedby` carries the instruction, and the
 * name says what would move, so a heading list still reads as workouts rather than as commands.
 *
 * ── Disabled stays FOCUSABLE, and its reason reaches more than a tooltip ─────────────────────
 * `aria-disabled`, never the attribute. A grip on a consumed phase — or on a phase holding one
 * training, where there is nowhere to move to — carries the reason it is closed, and the attribute
 * would drop it out of the tab order and take the explanation with it (view ADR §12).
 *
 * The reason is a rendered element that `aria-describedby` points at, REPLACING the keyboard
 * instruction while it exists. Two things were wrong with carrying it on `title` alone: a `title`
 * reaches neither a touch user nor a keyboard user who never hovers, and the description it left in
 * place said "Space picks up, arrows move" on a grip where Space does nothing at all.
 *
 * ── What the canon draws that this does NOT ──────────────────────────────────────────────────
 * `.sess.is-drag` tilts the moving card 1.4° with an accent border and a heavy shadow. That is the
 * MOVING image, and the moving image during a real drag is the browser's own: reproducing the tilt
 * means `setCustomNativeDragPreview` with a second React root mounted per gesture and a Safari
 * offscreen workaround, for a 1.4° rotation. The two states the canon's own note calls load-bearing
 * — the source becoming a dimmed "Sesja w ruchu" slot and the target taking an accent outline — are
 * both reproduced exactly. Flagged rather than silently dropped.
 */

import { useId, type KeyboardEvent, type Ref } from 'react';

import styles from './DragHandle.module.css';

/**
 * The six dots on their own, decorative — for a row where the drag is started from ANYWHERE on it
 * and the grip is a picture of "this can be picked up" rather than a control.
 *
 * The library rail is that row. Making its grip a button would put a SECOND tab stop on every one of
 * up to two hundred results while adding no keyboard capability at all — the row itself already
 * inserts, on Enter and on `E`, which is the rail's whole keyboard path. Two hundred extra tab stops
 * for an affordance a keyboard user cannot use is a net loss, so the dots stay a picture there and
 * the whole row is the draggable.
 */
export function DragDots({ className }: { readonly className?: string } = {}) {
  return (
    <span
      className={`${String(styles.grip)} ${String(styles.decorative)} ${className ?? ''}`}
      aria-hidden="true"
    >
      <span className={styles.dots}>
        <i />
        <i />
        <i />
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

interface DragHandleProps {
  readonly ref?: Ref<HTMLButtonElement>;
  /**
   * The SURFACE's placement of the grip — a margin, a grid area. Never its appearance: the dots, the
   * hit target and the three states belong to this module, and a caller restating one of them is
   * the near-duplicate the shared layer exists to prevent.
   */
  readonly className?: string;
  /** "Przenieś trening Push A" — what would move, never "drag handle". */
  readonly label: string;
  /** The id of the element carrying the keyboard instruction. */
  readonly describedBy?: string;
  /** Why it cannot act — a consumed phase. `null` when it can. */
  readonly disabledReason: string | null;
  /** True while this item is lifted by keyboard, so the grip reads as held. */
  readonly isLifted?: boolean;
  /**
   * The WIDE cut — KANON `raporty-2026-09-17`, which draws the same six dots as a 16px glyph in a
   * 22px column rather than the plan builder's 8.5px one. A size knob rather than a second grip:
   * the keyboard contract, the refusal, the lift state and the hit area are identical, and the
   * only thing that differs is how much room the row gives it. The plan builder passes nothing and
   * is unchanged.
   */
  readonly wide?: boolean;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * Bind `useKeyboardMove`'s own `onBlur`. A lift the trainer has tabbed away from can no longer be
   * steered by the arrows, so leaving it up wedges the canvas — see that hook's header.
   */
  readonly onBlur?: () => void;
}

export function DragHandle({
  ref,
  className,
  label,
  describedBy,
  disabledReason,
  isLifted = false,
  wide = false,
  onKeyDown,
  onBlur,
}: DragHandleProps) {
  const reasonId = useId();
  return (
    <button
      ref={ref}
      type="button"
      className={[String(styles.grip), wide ? String(styles.wide) : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      aria-label={label}
      // The refusal REPLACES the instruction rather than joining it: a grip that cannot lift must
      // not be described as one whose arrows move a card.
      aria-describedby={disabledReason !== null ? reasonId : describedBy}
      aria-disabled={disabledReason !== null ? 'true' : undefined}
      // ── `aria-grabbed` is INERT, and it stays anyway ─────────────────────────────────────────
      // It was carried here on the belief that it is what tells a screen-reader user the arrows
      // now mean something different. It is not: ARIA 1.2 DEPRECATED `aria-grabbed` along with the
      // whole `aria-dropeffect` pair, no current screen reader maps it, and nothing in this app
      // reads it but a stylesheet that already has `data-lifted`. What actually carries the state
      // is the canvas's live region, which announces the pickup, every stop and the cancellation.
      //
      // Kept rather than removed because it costs one attribute, remains valid HTML, and the two
      // e2e assertions that observe it are observing a real state under an inert name — deleting
      // it would mean editing passing tests for no user-visible gain. What is NOT kept is the
      // claim that it does the work.
      aria-grabbed={isLifted ? true : undefined}
      title={disabledReason ?? label}
      data-lifted={isLifted ? '' : undefined}
      onKeyDown={disabledReason === null ? onKeyDown : undefined}
      onBlur={onBlur}
    >
      {/* Six dots, exactly as the canon draws them. Decorative: the button's own name says what it
        * moves, and announcing the dots would say nothing. */}
      <span className={styles.dots} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
        <i />
        <i />
      </span>
      {/* Rendered, never drawn — a 9px column of dots has nowhere to put a sentence. The DIMMED
        * dots are the visual channel; this is the one assistive technology reads. */}
      {disabledReason !== null && (
        <span id={reasonId} className={styles.reason}>
          {disabledReason}
        </span>
      )}
    </button>
  );
}
